import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { api, isCode } from "../api/client.ts";
import type { CheckoutPage, CheckoutStatus } from "../api/types.ts";
import { LanguagePicker } from "../components/LanguagePicker.tsx";
import { IconAlert, IconCheck, IconExternal, IconInfo } from "../components/Icons.tsx";
import { ChainLogo, CopyButton, Spinner } from "../components/ui.tsx";
import { logo } from "../assets/images.ts";
import { fmt, rich, useT } from "../i18n/index.tsx";
import { chainName, formatTimeFull, formatUnits, tokenOf } from "../lib/format.ts";
import "./pay.css";

/** What players call each network ("USDT TRC20"), next to the chain's name. */
const STANDARD: Record<string, string> = { tron: "TRC20", bsc: "BEP20", eth: "ERC20" };
const networkLabel = (chain: string) => (STANDARD[chain] ? `${chainName(chain)} (${STANDARD[chain]})` : chainName(chain));
const amountText = (raw: string, decimals: number) => formatUnits(raw, decimals, 0, decimals);
const POLL_MS = 5000;

type Picked = { asset: string; chain: string; address: string };

/** Without a return_url, "Back" goes to the site the player came from (the referrer's origin; browsers usually send only
 *  that). Kept per token so a reload still knows it. The merchant's return_url always wins. */
function referrerReturn(token: string): string | null {
  const key = `icn-pay-ref:${token}`;
  try {
    const kept = sessionStorage.getItem(key);
    if (kept) return kept;
  } catch { /* storage blocked */ }
  try {
    const u = new URL(document.referrer);
    if (u.protocol !== "https:" || u.hostname === "icryptonow.com" || u.hostname.endsWith(".icryptonow.com")) return null;
    try { sessionStorage.setItem(key, u.origin); } catch { /* storage blocked */ }
    return u.origin;
  } catch {
    return null;
  }
}

/** Hosted payment page (public): /pay/<token>. The player picks a network, sends to their address, and is sent back. */
export function Pay() {
  const t = useT();
  const { token = "" } = useParams();
  const [page, setPage] = useState<CheckoutPage | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "invalid" | "error">("loading");
  const [picked, setPicked] = useState<Picked | null>(null);
  const [picking, setPicking] = useState<string | null>(null);
  const [pickError, setPickError] = useState(false);

  const load = useCallback(async () => {
    setState("loading");
    try {
      const p = await api.get<CheckoutPage>(`/pay/${encodeURIComponent(token)}`);
      if (!p.return_url) p.return_url = referrerReturn(token);
      setPage(p);
      setState("ready");
      if (p.networks.length === 1 && p.status === "open") void choose(p.networks[0]!.asset);
    } catch (e) {
      setState(isCode(e, "not_found") ? "invalid" : "error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => void load(), [load]);

  const choose = async (asset: string) => {
    setPicking(asset);
    setPickError(false);
    try {
      setPicked(await api.post<Picked>(`/pay/${encodeURIComponent(token)}/address`, { asset }));
    } catch (e) {
      if (isCode(e, "expired")) setPage((p) => (p ? { ...p, status: "expired" } : p));
      else setPickError(true);
    } finally {
      setPicking(null);
    }
  };

  // Live status: poll while the page is open and nothing is final yet.
  const final = page?.status === "completed" || page?.status === "expired";
  useEffect(() => {
    if (state !== "ready" || final) return;
    const id = window.setInterval(async () => {
      if (document.hidden) return;
      try {
        const s = await api.get<CheckoutStatus>(`/pay/${encodeURIComponent(token)}/status`);
        setPage((p) => (p ? { ...p, ...s } : p));
      } catch { /* keep the last state; the next poll tries again */ }
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [state, final, token]);

  useEffect(() => {
    document.title = page ? fmt(t.checkout.pageTitle, { brand: page.brand }) : "iCryptoNow";
  }, [page, t]);

  return (
    <div className="checkout">
      <header className="checkout-top">
        <img src={logo} alt="iCryptoNow" width={140} height={24} />
        <LanguagePicker compact />
      </header>
      <main className="checkout-main">
        {state === "loading" && !page && <div className="checkout-card checkout-center"><Spinner /></div>}
        {state === "invalid" && (
          <div className="checkout-card checkout-center">
            <h1 className="checkout-h1">{t.checkout.invalidTitle}</h1>
            <p className="muted">{t.checkout.invalidBody}</p>
          </div>
        )}
        {state === "error" && (
          <div className="checkout-card checkout-center">
            <p>{t.checkout.loadError}</p>
            <button type="button" className="btn btn-primary" onClick={() => void load()}>{t.checkout.retry}</button>
          </div>
        )}
        {page && state === "ready" && <Body page={page} picked={picked} picking={picking} pickError={pickError} onChoose={choose} onChange={() => setPicked(null)} />}
      </main>
      <footer className="checkout-foot">{t.checkout.secured}</footer>
    </div>
  );
}

function Body({ page, picked, picking, pickError, onChoose, onChange }: {
  page: CheckoutPage; picked: Picked | null; picking: string | null; pickError: boolean; onChoose: (asset: string) => void; onChange: () => void;
}) {
  const t = useT();
  const net = picked ? page.networks.find((n) => n.asset === picked.asset) : undefined;
  const latest = page.deposits[0];
  const token = net?.symbol ?? (page.networks[0] ? page.networks[0].symbol : "USDT");
  // 0 = choose network, 1 = send, 2 = seen on chain / confirmed
  const step = page.status === "completed" || latest ? 2 : picked && net ? 1 : 0;
  return (
    <>
    <div className="checkout-card checkout-head-card">
      <div className="checkout-head">
        <div>
          <div className="checkout-label">{t.checkout.payTo}</div>
          <h1 className="checkout-h1">{page.brand}</h1>
          {page.order_id && <div className="muted small checkout-order">{t.checkout.orderId} <span className="mono">{page.order_id}</span><CopyButton value={page.order_id} /></div>}
        </div>
        {page.amount && (
          <div className="checkout-amount">
            <div className="checkout-label">{t.checkout.amount}</div>
            <div className="checkout-amount-value">{page.amount} <span>{token}</span><CopyButton value={page.amount} /></div>
          </div>
        )}
      </div>
    </div>
    {page.status !== "expired" && <Steps step={step} done={page.status === "completed"} />}
    <div className="checkout-card">
      {page.status === "completed" && latest ? (
        <Done page={page} />
      ) : page.status === "expired" ? (
        <div className="checkout-state">
          <h2 className="checkout-h2">{t.checkout.expiredTitle}</h2>
          <p className="muted">{fmt(t.checkout.expiredBody, { brand: page.brand })}</p>
          {page.return_url && <a className="btn btn-primary" href={page.return_url}>{fmt(t.checkout.backTo, { brand: page.brand })}</a>}
        </div>
      ) : picked && net ? (
        <>
          <Address page={page} picked={picked} />
          <Progress page={page} />
          {page.networks.length > 1 && !latest && (
            <button type="button" className="btn btn-ghost checkout-change" onClick={onChange}>{t.checkout.changeNetwork}</button>
          )}
        </>
      ) : latest ? (
        <Progress page={page} />
      ) : (
        <div className="checkout-networks">
          <h2 className="checkout-h2">{t.checkout.chooseNetwork}</h2>
          <p className="muted small">{t.checkout.chooseNetworkHint}</p>
          <ul>
            {page.networks.map((n) => (
              <li key={n.asset}>
                <button type="button" className="checkout-network" onClick={() => onChoose(n.asset)} disabled={picking !== null}>
                  <ChainLogo chain={n.chain} size={28} />
                  <span className="checkout-network-name">
                    <strong>{n.symbol} · {networkLabel(n.chain)}</strong>
                    <span className="muted small">{fmt(t.checkout.minimum, { amount: amountText(n.min_amount, n.decimals), token: n.symbol })}</span>
                  </span>
                  {n.chain === "bsc" && <span className="checkout-tag">{t.checkout.recommended}</span>}
                  {picking === n.asset && <Spinner />}
                </button>
              </li>
            ))}
          </ul>
          {pickError && <p className="checkout-error">{t.checkout.loadError}</p>}
          <p className="muted small">{fmt(t.checkout.validUntil, { time: formatTimeFull(page.expires_at) })}</p>
        </div>
      )}
    </div>
    </>
  );
}

function Steps({ step, done }: { step: number; done: boolean }) {
  const t = useT();
  return (
    <ol className="checkout-steps">
      {t.checkout.steps.map((label, i) => {
        const state = i < step || (done && i === step) ? "done" : i === step ? "now" : "next";
        return (
          <li key={label} className={state} aria-current={state === "now" ? "step" : undefined}>
            <span className="checkout-step-dot">{state === "done" ? <IconCheck size={14} /> : i + 1}</span>
            <span className="checkout-step-label">{label}</span>
          </li>
        );
      })}
    </ol>
  );
}

/** "59:12" until the link expires (h:mm:ss above an hour); null once it has passed. */
function useCountdown(until: string): string | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const s = Math.floor((new Date(until).getTime() - now) / 1000);
  if (!(s > 0)) return null;
  const pad = (n: number) => String(n).padStart(2, "0");
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return h ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
}

function Address({ page, picked }: { page: CheckoutPage; picked: Picked }) {
  const t = useT();
  const n = page.networks.find((x) => x.asset === picked.asset)!;
  const min = amountText(n.min_amount, n.decimals);
  const network = networkLabel(n.chain);
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    import("qrcode")
      .then(({ default: QRCode }) => QRCode.toDataURL(picked.address, { margin: 1, width: 480, errorCorrectionLevel: "H", color: { dark: "#0b1e46", light: "#ffffff" } }))
      .then((u) => alive && setQr(u))
      .catch(() => alive && setQr(null));
    return () => {
      alive = false;
    };
  }, [picked.address]);
  return (
    <div className="checkout-send">
      <h2 className="checkout-h2"><ChainLogo chain={n.chain} size={22} /> {fmt(t.checkout.sendTitle, { token: n.symbol, network })}</h2>
      <p>{page.amount
        ? rich(t.checkout.sendAmount, { b: (c) => <strong>{c}</strong> }, { amount: page.amount, token: n.symbol })
        : rich(t.checkout.sendAny, { b: (c) => <strong>{c}</strong> }, { min, token: n.symbol })}</p>
      <div className="checkout-qr">{qr ? <><img src={qr} alt={t.checkout.qrAlt} width={232} height={232} /><span className="checkout-qr-logo"><ChainLogo chain={n.chain} size={30} /></span></> : <Spinner />}</div>
      <div className="checkout-label">{t.checkout.addressLabel}</div>
      <div className="checkout-address-box"><code className="checkout-address" style={{ ["--n" as string]: picked.address.length }}>{picked.address}</code></div>
      <CopyButton value={picked.address} label={t.checkout.copyAddress} showText className="checkout-copy" />
      <p className="checkout-warn"><IconAlert size={20} /><strong>{fmt(t.checkout.warnNetwork, { token: n.symbol, network })}</strong></p>
      <ul className="checkout-notes">
        <li><IconInfo size={16} />{fmt(t.checkout.warnMinimum, { min, token: n.symbol })}</li>
        <li><IconInfo size={16} />{t.checkout.warnAddress}</li>
      </ul>
    </div>
  );
}

function Progress({ page }: { page: CheckoutPage }) {
  const t = useT();
  const d = page.deposits[0];
  const left = useCountdown(page.expires_at);
  if (!d) {
    return (
      <div className="checkout-progress">
        <span className="checkout-pulse" aria-hidden="true" />
        <div className="checkout-progress-text">
          <div className="checkout-progress-row"><strong>{t.checkout.waiting}</strong>{left && <span className="muted small">{fmt(t.checkout.expiresIn, { time: left })}</span>}</div>
          <div className="muted small">{t.checkout.waitingHint}</div>
        </div>
      </div>
    );
  }
  const amount = `${amountText(d.amount, d.decimals)}`;
  const token = tokenOf(d.asset);
  if (d.status === "below_min" || d.status === "flagged") {
    return (
      <div className="checkout-progress checkout-progress-warn">
        <p>{d.status === "below_min" ? fmt(t.checkout.belowMin, { amount, token, brand: page.brand }) : fmt(t.checkout.flagged, { brand: page.brand })}</p>
        <TxLink url={d.explorer_url} />
      </div>
    );
  }
  return (
    <div className="checkout-progress">
      <Spinner />
      <div>
        <strong>{t.checkout.detected}</strong> <span className="mono">{amount} {token}</span>
        <div className="muted small">{t.checkout.detectedHint}</div>
        <TxLink url={d.explorer_url} />
      </div>
    </div>
  );
}

function Done({ page }: { page: CheckoutPage }) {
  const t = useT();
  const d = page.deposits.find((x) => x.status === "confirmed")!;
  const [left, setLeft] = useState(page.return_url ? 8 : 0);
  const timer = useRef<number | null>(null);
  useEffect(() => {
    if (!page.return_url) return;
    timer.current = window.setInterval(() => setLeft((n) => n - 1), 1000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [page.return_url]);
  useEffect(() => {
    if (page.return_url && left <= 0) window.location.assign(page.return_url);
  }, [left, page.return_url]);
  return (
    <div className="checkout-state checkout-done">
      <span className="checkout-done-icon"><IconCheck size={28} /></span>
      <h2 className="checkout-h2">{t.checkout.completed}</h2>
      <p>{fmt(t.checkout.completedBody, { amount: amountText(d.amount, d.decimals), token: tokenOf(d.asset), brand: page.brand })}</p>
      <TxLink url={d.explorer_url} />
      {page.return_url && (
        <>
          <a className="btn btn-primary" href={page.return_url}>{fmt(t.checkout.backTo, { brand: page.brand })}</a>
          <p className="muted small">{fmt(t.checkout.redirecting, { n: Math.max(left, 0) })}</p>
        </>
      )}
    </div>
  );
}

function TxLink({ url }: { url: string | null }) {
  const t = useT();
  if (!url) return null;
  return <a className="checkout-tx" href={url} target="_blank" rel="noreferrer noopener">{t.checkout.viewTx} <IconExternal size={13} /></a>;
}
